import { PSYCHOLOGY_API_CORE_KEY, API_CORE_URL, API_CORE_CONFIG } from './config';
import { getValidToken, redirectToLogin, hasValidSession } from './auth';
import { toastUtils } from '../useToast';

import { trackNetworkFailure, trackHttpStatus, rememberRequest, type RequestContext } from '../../utils/activityHttpErrors';
export type QueryParams = Record<string, unknown>;

export interface SecureRequestOptions extends RequestInit {
  timeout?: number;
  retries?: number;
  successMessage?: string;
  errorMessage?: string;
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
  loadingMessage?: string;
}

function buildQuery(params?: QueryParams): string {
  if (!params) return '';
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) usp.set(key, String(value));
  });
  const s = usp.toString();
  return s ? `?${s}` : '';
}

function createTimeoutController(timeoutMs: number): AbortController {
  const controller = new AbortController();
  setTimeout(() => controller.abort(), timeoutMs);
  return controller;
}

async function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function secureRequest(path: string, options: SecureRequestOptions = {}): Promise<Response> {
  const {
    timeout = API_CORE_CONFIG.TIMEOUT,
    retries = API_CORE_CONFIG.MAX_RETRIES,
    ...init
  } = options;

  if (!hasValidSession()) {
    redirectToLogin();
    throw new Error('Sesión expirada. Por favor, inicia sesión nuevamente.');
  }

  const url = `${API_CORE_URL}${path}`;
  const token = getValidToken();

  if (!token) {
    redirectToLogin();
    throw new Error('No hay token válido. Por favor, inicia sesión nuevamente.');
  }

  const headers = new Headers(init?.headers || {});
  
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  
  if (PSYCHOLOGY_API_CORE_KEY) {
    headers.set('x-api-key', PSYCHOLOGY_API_CORE_KEY);
  }
  
  headers.set('Authorization', `Bearer ${token}`);

  const makeRequest = async (attempt: number): Promise<Response> => {
    const controller = createTimeoutController(timeout);
    const ctx: RequestContext = { client: 'api-core', method: init?.method, url, startedAt: Date.now(), retried: attempt > 0 };
    
    try {
      const response = await fetch(url, {
        ...init,
        headers,
        signal: controller.signal,
      });
      rememberRequest(response, ctx);

      return response;

    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          trackNetworkFailure('timeout', ctx);
          throw new Error(`Timeout: La request tardó más de ${timeout}ms`);
        }
        
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {          
          if (attempt < retries) {
            const delayMs = API_CORE_CONFIG.RETRY_DELAY * Math.pow(2, attempt);
            await delay(delayMs);
            return makeRequest(attempt + 1);
          }
          
          trackNetworkFailure('network_error', ctx);
          throw new Error('Error de conexión: No se pudo conectar con el servidor');
        }
      }
      
      throw error;
    }
  };

  return makeRequest(0);
}

async function parseJson<T>(resp: Response): Promise<T> {
  // Si es 304 Not Modified, devolver un array vacío para evitar errores
  if (resp.status === 304) {
    // Respuesta 304 Not Modified - Usando datos en caché
    return ([] as unknown) as T;
  }
  
  const raw = await resp.text();
  
  if (!resp.ok) {
    trackHttpStatus(resp.status, resp); // Métricas de Actividad: solo 5xx y 401/403 (código cerrado)
    if (resp.status === 401 || resp.status === 403) {
      redirectToLogin();
      throw new Error('Sesión expirada. Por favor, inicia sesión nuevamente.');
    }
    
    let errorMessage = `Error HTTP ${resp.status}`;
    try {
      const errorData = JSON.parse(raw);
      if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (errorData?.error) {
        errorMessage = errorData.error;
      }
    } catch {
      errorMessage = raw || errorMessage;
    }
    
    switch (resp.status) {
      case 400:
        throw new Error(`Datos inválidos: ${errorMessage}`);
      case 404:
        throw new Error('Recurso no encontrado');
      case 429:
        throw new Error('Demasiadas solicitudes. Intenta más tarde');
      case 500:
      case 502:
      case 503:
        console.error(`[secureHttp] ${resp.status} en ${resp.url} →`, raw);
        throw new Error('Error del servidor. Intenta más tarde');
      default:
        throw new Error(errorMessage);
    }
  }
  
  if (!raw || raw.trim().length === 0) {
    return ([] as unknown) as T;
  }
  
  try {
    const parsed = JSON.parse(raw);
    return parsed as T;
  } catch (parseError) {
    console.warn('⚠️ No se pudo parsear JSON, devolviendo texto plano');
    return raw as unknown as T;
  }
}

export async function secureApiGet<T>(
  path: string, 
  params?: QueryParams, 
  options?: SecureRequestOptions
): Promise<T> {
  const {
    successMessage,
    errorMessage,
    showSuccessToast = false,
    showErrorToast = true,
    loadingMessage,
    timeout,
    retries,
    ...restOptions
  } = options || {};

  let loadingToast: string | number | undefined;

  try {
    if (loadingMessage) {
      loadingToast = toastUtils.loading(loadingMessage);
    }

    const q = buildQuery(params);
    const resp = await secureRequest(`${path}${q}`, { 
      ...restOptions, 
      method: 'GET', 
      timeout, 
      retries 
    });
    const result = await parseJson<T>(resp);

    if (loadingToast) {
      toastUtils.dismiss(loadingToast);
    }

    if (showSuccessToast && successMessage) {
      toastUtils.success(successMessage);
    }

    return result;
  } catch (error) {
    if (loadingToast) {
      toastUtils.dismiss(loadingToast);
    }

    const message = error instanceof Error ? error.message : 'Error desconocido';
    
    if (showErrorToast) {
      toastUtils.error(errorMessage || message);
    }

    throw error;
  }
}

export async function secureApiPost<T, B = unknown>(
  path: string, 
  body?: B, 
  options?: SecureRequestOptions
): Promise<T> {
  const {
    successMessage,
    errorMessage,
    showSuccessToast = true,
    showErrorToast = true,
    loadingMessage,
    timeout,
    retries,
    ...restOptions
  } = options || {};

  let loadingToast: string | number | undefined;

  try {
    if (loadingMessage) {
      loadingToast = toastUtils.loading(loadingMessage);
    }

    const resp = await secureRequest(path, { 
      ...restOptions, 
      method: 'POST', 
      body: body ? JSON.stringify(body) : undefined,
      timeout,
      retries
    });
    const result = await parseJson<T>(resp);

    if (loadingToast) {
      toastUtils.dismiss(loadingToast);
    }

    if (showSuccessToast && successMessage) {
      toastUtils.success(successMessage);
    }

    return result;
  } catch (error) {
    if (loadingToast) {
      toastUtils.dismiss(loadingToast);
    }

    const message = error instanceof Error ? error.message : 'Error desconocido';
    
    if (showErrorToast) {
      toastUtils.error(errorMessage || message);
    }

    throw error;
  }
}

export async function secureApiPut<T, B = unknown>(
  path: string, 
  body?: B, 
  options?: SecureRequestOptions
): Promise<T> {
  const {
    successMessage,
    errorMessage,
    showSuccessToast = true,
    showErrorToast = true,
    loadingMessage,
    timeout,
    retries,
    ...restOptions
  } = options || {};

  let loadingToast: string | number | undefined;

  try {
    if (loadingMessage) {
      loadingToast = toastUtils.loading(loadingMessage);
    }

    const resp = await secureRequest(path, { 
      ...restOptions, 
      method: 'PUT', 
      body: body ? JSON.stringify(body) : undefined,
      timeout,
      retries
    });
    const result = await parseJson<T>(resp);

    if (loadingToast) {
      toastUtils.dismiss(loadingToast);
    }

    if (showSuccessToast && successMessage) {
      toastUtils.success(successMessage);
    }

    return result;
  } catch (error) {
    if (loadingToast) {
      toastUtils.dismiss(loadingToast);
    }

    const message = error instanceof Error ? error.message : 'Error desconocido';
    
    if (showErrorToast) {
      toastUtils.error(errorMessage || message);
    }

    throw error;
  }
}

export async function secureApiPatch<T, B = unknown>(
  path: string,
  body?: B,
  options?: SecureRequestOptions
): Promise<T> {
  const {
    successMessage,
    errorMessage,
    showSuccessToast = true,
    showErrorToast = true,
    loadingMessage,
    timeout,
    retries,
    ...restOptions
  } = options || {};

  let loadingToast: string | number | undefined;

  try {
    if (loadingMessage) {
      loadingToast = toastUtils.loading(loadingMessage);
    }

    const resp = await secureRequest(path, {
      ...restOptions,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
      timeout,
      retries
    });
    const result = await parseJson<T>(resp);

    if (loadingToast) {
      toastUtils.dismiss(loadingToast);
    }

    if (showSuccessToast && successMessage) {
      toastUtils.success(successMessage);
    }

    return result;
  } catch (error) {
    if (loadingToast) {
      toastUtils.dismiss(loadingToast);
    }

    const message = error instanceof Error ? error.message : 'Error desconocido';

    if (showErrorToast) {
      toastUtils.error(errorMessage || message);
    }

    throw error;
  }
}

export async function secureApiDelete<T, B = unknown>(
  path: string,
  body?: B,
  options?: SecureRequestOptions
): Promise<T> {
  const {
    successMessage,
    errorMessage,
    showSuccessToast = true,
    showErrorToast = true,
    loadingMessage,
    timeout,
    retries,
    ...restOptions
  } = options || {};

  let loadingToast: string | number | undefined;

  try {
    if (loadingMessage) {
      loadingToast = toastUtils.loading(loadingMessage);
    }

    const resp = await secureRequest(path, {
      ...restOptions,
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
      timeout,
      retries
    });
    const result = await parseJson<T>(resp);

    if (loadingToast) {
      toastUtils.dismiss(loadingToast);
    }

    if (showSuccessToast && successMessage) {
      toastUtils.success(successMessage);
    }

    return result;
  } catch (error) {
    if (loadingToast) {
      toastUtils.dismiss(loadingToast);
    }

    const message = error instanceof Error ? error.message : 'Error desconocido';

    if (showErrorToast) {
      toastUtils.error(errorMessage || message);
    }

    throw error;
  }
}

export const secureHttp = {
  get: secureApiGet,
  post: secureApiPost,
  put: secureApiPut,
  patch: secureApiPatch,
  delete: secureApiDelete
};

